import { useState, FC } from 'react'
import { Card, Divider, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../../../components/custom_components/Modal'
import { EmployeeDisplayTinyInfo, MeetingRequestDto, MeetingType } from '../../../types'
import { MeetingTag } from '../../../components/custom_components/MeetingTag'
import PerText from '../../../components/custom_components/Text'
import PerSpacer from '../../../components/custom_components/Spacer'

const { TextArea } = Input

interface RequestMeetingModalSubheadingProps {
  requester: EmployeeDisplayTinyInfo
  meetingType: MeetingType
}
export const RequestedMeetingModalSubheading: FC<RequestMeetingModalSubheadingProps> = ({ requester, meetingType }) => {
  const { t } = useTranslation()
  return (
    <>
      {t(`meetingView.requestedMeetings.modal.shared.subtitle`, {
        employeeFullName: `${requester.firstName} ${requester.lastName}`,
      })}
      <PerSpacer ml="xxs" display="inline-block">
        <MeetingTag type={meetingType} />
      </PerSpacer>
    </>
  )
}

interface RequestedMeetingModalReasonProps {
  reason: string
}
export const RequestMeetingModalReason: FC<RequestedMeetingModalReasonProps> = ({ reason }) => {
  return (
    <>
      <Card bordered={false}>
        <PerText appearance="quote">{reason}</PerText>
      </Card>
      <Divider />
    </>
  )
}

interface RequestMeetingDeclineModalProps {
  isOpen: boolean
  onClose: () => void
  onDeclineRequest: (requestMeetingId: number, reasonOfRejection: string, reviewerId: number) => Promise<string | undefined>
  requestedMeeting: MeetingRequestDto
}
export const RequestMeetingDeclineModal: FC<RequestMeetingDeclineModalProps> = ({
  isOpen,
  onClose,
  onDeclineRequest,
  requestedMeeting,
}) => {
  const { t } = useTranslation()
  const [reasonForMeetingCancel, setReasonForMeetingCancel] = useState('')
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const declineRequest = async (meetingId: number, reason: string, reviewerId: number) => {
    toggleSubmitting(true)
    const res = await onDeclineRequest(meetingId, reason, reviewerId)
    if (!res) {
      setError(undefined)
    } else {
      setError(t('global.notifications.error.description'))
    }
    toggleSubmitting(false)
  }

  return (
    <PerModal
      title={{
        heading: t('meetingView.requestedMeetings.modal.decline.title'),
        subheading: <RequestedMeetingModalSubheading requester={requestedMeeting.requester} meetingType={requestedMeeting.type} />,
      }}
      size="small"
      centered
      visible={isOpen}
      onCancel={() => {
        onClose()
        setReasonForMeetingCancel('')
      }}
      footer={{
        cancelBtn: {
          onClick: () => {
            onClose()
            setReasonForMeetingCancel('')
          },
        },
        customBtns: [
          {
            text: t('global.button.decline'),
            onClick: async () => {
              await declineRequest(requestedMeeting.id, reasonForMeetingCancel, requestedMeeting.reviewer.id)
              setReasonForMeetingCancel('')
            },
            color: 'error',
            icon: 'close_stroke',
            disabled: !reasonForMeetingCancel,
            isProcessing: { active: isSubmitting },
          },
        ],
      }}
    >
      <RequestMeetingModalReason reason={requestedMeeting.reasonForRequest} />

      <PerText appearance="subheading">{t('meetingView.requestedMeetings.modal.decline.reasonOfRejection.label')}</PerText>
      <TextArea
        value={reasonForMeetingCancel}
        placeholder={t('meetingView.requestedMeetings.modal.decline.reasonOfRejection.placeholder', {
          employeeName: requestedMeeting.requester.firstName,
        })}
        onChange={(e) => {
          e.preventDefault()
          setReasonForMeetingCancel(e.target.value)
        }}
        rows={4}
      />
      {error && (
        <PerText color="error" fontSize="m" mt="m">
          {error}
        </PerText>
      )}
    </PerModal>
  )
}
